import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { COMMERCIAL_PRICING } from '@connexio/shared/constants';
import type { PublicCommercialConfig } from '@connexio/shared';
import { useLocale } from '@/hooks/useLocale';
import { formatNumber } from '@/lib/formatters';
import { ContactEmailNote } from './ContactEmailNote';
import { PrimaryCta } from './PrimaryCta';
import { Section, SectionHeading } from './Section';
import styles from './PricingCard.module.css';
import { cn } from '@/lib/utils';

interface PricingCardProps {
  pricing?: PublicCommercialConfig['pricing'];
  trialDays?: number;
  signupEnabled?: boolean;
}

function formatSek(amount: number, locale: 'sv' | 'en') {
  return formatNumber(amount, locale, {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export function PricingCard({
  pricing = COMMERCIAL_PRICING,
  trialDays = COMMERCIAL_PRICING.trialDays,
  signupEnabled = false,
}: PricingCardProps) {
  const { t } = useTranslation('marketing');
  const { locale } = useLocale();
  const exVat = formatSek(pricing.amountExVatSek, locale);
  const incVat = formatSek(pricing.amountIncVatSek, locale);

  const included = [
    t('pricing.includes'),
    t('pricing.monthly'),
    // No trial promise while self-service sign-up is closed.
    ...(signupEnabled ? [t('pricing.trial', { days: trialDays })] : []),
    t('pricing.cancel'),
  ];

  return (
    <Section id="pricing" tone="dark" labelledBy="pricing-title">
      <SectionHeading
        id="pricing-title"
        eyebrow={t('pricing.eyebrow')}
        title={t('pricing.title')}
        align="center"
      />

      {/* Price and the trial button sit side by side with what is included from lg,
          so the whole offer is readable without scrolling inside the block. */}
      <div className={styles.grid}>
        <div className={styles.box}>
          <p>
            <span className={styles.exVat}>
              {t('pricing.exVat', { amount: exVat })}
            </span>
            <span className={cn(styles.perUser, 'text-body-l')}>
              {t('pricing.perUser')}
            </span>
            <span className={cn(styles.incVat, 'text-body-m')}>
              {t('pricing.incVat', { amount: incVat })}
            </span>
          </p>
          <div className={styles.box2}>
            <PrimaryCta signupEnabled={signupEnabled} className={styles.primaryCta} />
            {signupEnabled ? null : <ContactEmailNote className={styles.contactEmailNote} />}
          </div>
        </div>

        <ul className={styles.list}>
          {included.map((line) => (
            <li key={line} className={styles.item}>
              <Check className={styles.icon} aria-hidden="true" />
              <span className={styles.text}>{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
}
